import { useEffect, useState } from "react";
import { Megaphone } from "lucide-react";
import api from "@/api";

const SOURCE_STYLES = {
  cetf: "bg-emerald-600 text-white",
  feed: "bg-slate-600 text-white",
  toury: "bg-sky-600 text-white",
  janville: "bg-amber-500 text-slate-900",
  beauce: "bg-violet-600 text-white",
};

export default function NewsTicker() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    api.get("/ticker").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => setItems([]));
  }, []);

  useEffect(() => {
    if (items.length < 2) return undefined;
    const t = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIdx((i) => (i + 1) % items.length);
        setVisible(true);
      }, 450);
    }, 5000);
    return () => clearInterval(t);
  }, [items.length]);

  if (items.length === 0) return null;
  const item = items[idx % items.length];
  const mediaUrl = item.media_url
    ? (item.media_url.startsWith("http") ? item.media_url : `${process.env.REACT_APP_BACKEND_URL}${item.media_url}`)
    : "";

  return (
    <section className="relative z-[1] overflow-hidden bg-gradient-to-r from-[#0b1220] via-[#0d2b25] to-[#0b1220] text-white border-y border-emerald-900/40" data-testid="news-ticker">
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true"
        style={{ background: "radial-gradient(640px 130px at 18% 50%, rgba(16,185,129,0.16), transparent 70%)" }} />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-emerald-400/50 to-transparent" aria-hidden="true" />
      <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-emerald-400/30 to-transparent" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center gap-4">
        <span className="hidden sm:inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-emerald-400 shrink-0">
          <Megaphone className="h-4 w-4" />Le fil local
        </span>
        <div
          className={`flex-1 min-w-0 flex items-center gap-3.5 transition-all duration-500 ease-in-out ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1.5"}`}
          data-testid="ticker-item"
        >
          {mediaUrl && item.media_type === "image" && (
            <img src={mediaUrl} alt="" className="h-11 w-11 rounded-lg object-cover shrink-0 border border-white/20" data-testid="ticker-media" />
          )}
          {mediaUrl && item.media_type === "video" && (
            <video src={mediaUrl} autoPlay muted loop playsInline
              className="h-11 w-16 rounded-lg object-cover shrink-0 border border-white/20" data-testid="ticker-media" />
          )}
          {item.link ? (
            <a href={item.link} target="_blank" rel="noopener noreferrer"
              className="text-sm sm:text-base truncate hover:text-emerald-300 hover:underline" data-testid="ticker-text">
              {item.text}
            </a>
          ) : (
            <p className="text-sm sm:text-base truncate" data-testid="ticker-text">{item.text}</p>
          )}
          <span
            className={`ml-auto shrink-0 text-[11px] font-semibold px-2.5 py-1 rounded-full ${SOURCE_STYLES[item.source] || SOURCE_STYLES.cetf}`}
            data-testid="ticker-source-badge"
          >
            {item.source_label}
          </span>
        </div>
        {items.length > 1 && (
          <div className="hidden md:flex items-center gap-1 shrink-0" data-testid="ticker-dots">
            {items.slice(0, 8).map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIdx(i)}
                aria-label={`Actualité ${i + 1}`}
                data-testid={`ticker-dot-${i}`}
                className={`h-1.5 rounded-full transition-all ${i === idx % items.length ? "w-4 bg-emerald-400" : "w-1.5 bg-slate-600 hover:bg-slate-500"}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
